import { PricingCustomerListRow } from '@sales-portal/domain/pricing/screens/customers/blocs/transformers/use-customer-list-transformer';
import { PricingEdit } from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';

export interface CustomerEditDelta {
  grossProfitImpactInMicros: number;
  revenueDeltaInMicros: number;
}

export interface CustomerListSummaryTotals {
  avgMarginPercentage: number | null;
  customerCount: number;
  totalEditsImpactInMicros: number;
  totalGrossProfitInMicros: number;
  totalLineItems: number;
  totalRevenueInMicros: number;
}

export const aggregateEditDeltasByCustomer = (
  edits: PricingEdit[],
): Record<string, CustomerEditDelta> =>
  edits.reduce<Record<string, CustomerEditDelta>>((acc, edit) => {
    const entry = acc[edit.restaurantUUID] ?? {
      grossProfitImpactInMicros: 0,
      revenueDeltaInMicros: 0,
    };
    entry.grossProfitImpactInMicros += edit.grossProfitImpactInMicros;
    entry.revenueDeltaInMicros += edit.priceDeltaInMicros * edit.weeklyVolume;
    acc[edit.restaurantUUID] = entry;
    return acc;
  }, {});

export const applyEditDeltasToCustomerRows = (
  rows: PricingCustomerListRow[],
  deltas: Record<string, CustomerEditDelta>,
): PricingCustomerListRow[] =>
  rows.map((row) => {
    const delta = deltas[row.restaurantUUID];
    if (!delta) {
      return row;
    }
    const newRevenue = row.revenueInMicros + delta.revenueDeltaInMicros;
    const newGrossProfit =
      row.grossProfitInMicros + delta.grossProfitImpactInMicros;
    return {
      ...row,
      grossProfitInMicros: newGrossProfit,
      marginPercentage:
        newRevenue > 0
          ? (newGrossProfit / newRevenue) * 100
          : row.marginPercentage,
      revenueInMicros: newRevenue,
    };
  });

export const computeCustomerListSummaryTotals = (
  rows: PricingCustomerListRow[],
  editDeltasByCustomer: Record<string, CustomerEditDelta>,
): CustomerListSummaryTotals => {
  let totalLineItems = 0;
  let totalRevenueInMicros = 0;
  let totalGrossProfitInMicros = 0;
  let totalEditsImpactInMicros = 0;
  let marginSum = 0;
  let marginCount = 0;
  for (const row of rows) {
    totalLineItems += row.lineItems;
    totalRevenueInMicros += row.revenueInMicros;
    totalGrossProfitInMicros += row.grossProfitInMicros;
    totalEditsImpactInMicros +=
      editDeltasByCustomer[row.restaurantUUID]?.grossProfitImpactInMicros ?? 0;
    if (row.marginPercentage !== null) {
      marginSum += row.marginPercentage;
      marginCount += 1;
    }
  }
  return {
    avgMarginPercentage: marginCount > 0 ? marginSum / marginCount : null,
    customerCount: rows.length,
    totalEditsImpactInMicros,
    totalGrossProfitInMicros,
    totalLineItems,
    totalRevenueInMicros,
  };
};
